/**
 * Session tokens (SPEC §5.4).
 *
 * A token is bound to `room + seat`. It lives in localStorage, and returning to
 * the room URL replays it in the `join` message so the server re-binds the
 * existing seat and answers with a full snapshot. The user sees nothing.
 *
 * Tokens for several rooms coexist under one key so that "play again" in room
 * ABCD and a fresh room EFGH on the same phone do not fight. Entries expire
 * after the room's own idle TTL, because a token for a purged room is worse
 * than no token — it would try to reclaim a seat that no longer exists.
 */
import { ROOM_IDLE_TTL_MS, SESSION_STORAGE_KEY } from '@idgame/protocol';

export interface StoredSession {
  roomCode: string;
  sessionToken: string;
  playerId: string;
  /** ms epoch of the last successful use. */
  updatedAt: number;
  /**
   * Whether this seat has completed consent → camera → name, so a reconnect can
   * skip onboarding (SPEC §5.4).
   *
   * A **boolean only**. The player's name and photo are deliberately not here:
   * SPEC §6's posture is no durable personal data anywhere, and the session
   * token is the only thing authorised for localStorage. Set from the server's
   * roster rather than optimistically, so a `set_profile` that never landed
   * cannot make a future reconnect skip a step it should not.
   */
  onboarded?: boolean;
}

type SessionMap = Record<string, StoredSession>;

/** Sessions older than the room TTL cannot possibly still be reclaimable. */
const MAX_AGE_MS = ROOM_IDLE_TTL_MS;

/**
 * Host tokens (SPEC §5.5) live under their own key rather than inside a
 * session.
 *
 * A room is created a whole onboarding flow before it is joined, so the token
 * exists while there is still no seat to hang it on — and `listSessions()`
 * feeds the "Rejoin room ABCD" button, which must not offer a room this device
 * only opened and walked away from. Two keys, two lifetimes, no seatless
 * entries in the seat list.
 */
const HOST_STORAGE_KEY = `${SESSION_STORAGE_KEY}.host`;

interface StoredHostClaim {
  hostToken: string;
  updatedAt: number;
}

/**
 * Stand-in for `localStorage` when there isn't one.
 *
 * A private window, a browser set to clear on close, iOS evicting a site's
 * data: the seat then survives nothing, not even the socket blip that happens
 * when a phone locks — every reconnect arrives as a stranger and the player
 * gets the seat picker instead of their game. This keeps the same two keys in
 * memory instead, so the seat lasts as long as the tab does, which is as long
 * as the game does. A reload still loses it, and that is what `claim_seat`
 * exists for (SPEC §5.4).
 *
 * Not a cache in front of the real thing — it is only ever consulted when the
 * real thing is unavailable, so there is nothing to keep in sync.
 */
const fallback = new Map<string, string>();

const memoryStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem: (key) => fallback.get(key) ?? null,
  setItem: (key, value) => void fallback.set(key, value),
  removeItem: (key) => void fallback.delete(key),
};

type SessionStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function storage(): SessionStore {
  try {
    const store = globalThis.localStorage;
    if (!store) return memoryStorage;
    // Safari private mode throws on write, not on access.
    const probe = '__idgame_probe__';
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch {
    return memoryStorage;
  }
}

function readAll(): SessionMap {
  const store = storage();
  try {
    const raw = store.getItem(SESSION_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const now = Date.now();
    const out: SessionMap = {};
    for (const [code, value] of Object.entries(parsed as Record<string, unknown>)) {
      const entry = coerce(code, value);
      if (entry && now - entry.updatedAt < MAX_AGE_MS) out[code] = entry;
    }
    return out;
  } catch {
    return {};
  }
}

function coerce(roomCode: string, value: unknown): StoredSession | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.sessionToken !== 'string' || v.sessionToken.length === 0) return null;
  if (typeof v.playerId !== 'string') return null;
  return {
    roomCode,
    sessionToken: v.sessionToken,
    playerId: v.playerId,
    updatedAt: typeof v.updatedAt === 'number' ? v.updatedAt : 0,
    onboarded: v.onboarded === true,
  };
}

function writeAll(map: SessionMap): void {
  const store = storage();
  try {
    store.setItem(SESSION_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota: the session simply won't survive a reload */
  }
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                  */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* Host claim                                                                  */
/* -------------------------------------------------------------------------- */

function readHostClaims(): Record<string, StoredHostClaim> {
  const store = storage();
  try {
    const raw = store.getItem(HOST_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const now = Date.now();
    const out: Record<string, StoredHostClaim> = {};
    for (const [code, value] of Object.entries(parsed as Record<string, unknown>)) {
      const entry = value as Record<string, unknown> | null;
      const token = entry?.hostToken;
      const at = typeof entry?.updatedAt === 'number' ? entry.updatedAt : 0;
      if (typeof token === 'string' && token.length > 0 && now - at < MAX_AGE_MS) {
        out[code] = { hostToken: token, updatedAt: at };
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeHostClaims(map: Record<string, StoredHostClaim>): void {
  const store = storage();
  try {
    store.setItem(HOST_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota: the room falls back to first-in */
  }
}

/**
 * Remember that this device created a room. Called the moment
 * `POST /api/rooms` answers, before the creator has a name or a photo.
 */
export function saveHostToken(roomCode: string, hostToken: string): void {
  const map = readHostClaims();
  map[normalise(roomCode)] = { hostToken, updatedAt: Date.now() };
  writeHostClaims(map);
}

/** The claim to replay in `join`, or undefined on every non-creating device. */
export function loadHostToken(roomCode: string): string | undefined {
  return readHostClaims()[normalise(roomCode)]?.hostToken;
}

/**
 * Give up the claim. Called when the player deliberately leaves — walking out
 * of a room you started and walking back in later should not silently take the
 * host seat off whoever is running it now.
 *
 * Deliberately *not* called from `clearSession`: `game_ended` clears the seat
 * while the room is still alive for "play again", and a creator who reloads in
 * that window is still the creator.
 */
export function clearHostToken(roomCode: string): void {
  const code = normalise(roomCode);
  const map = readHostClaims();
  if (!(code in map)) return;
  delete map[code];
  writeHostClaims(map);
}

/* -------------------------------------------------------------------------- */

/** The stored seat for a room, or null if this device has never held one. */
export function loadSession(roomCode: string): StoredSession | null {
  return readAll()[normalise(roomCode)] ?? null;
}

/** The token to replay in `join`, or undefined for a first-time join. */
export function loadSessionToken(roomCode: string): string | undefined {
  return loadSession(roomCode)?.sessionToken;
}

export function saveSession(input: Omit<StoredSession, 'updatedAt'>): StoredSession {
  const code = normalise(input.roomCode);
  const map = readAll();
  const previous = map[code];
  const entry: StoredSession = {
    ...previous,
    ...input,
    roomCode: code,
    updatedAt: Date.now(),
  };
  map[code] = entry;
  writeAll(map);
  return entry;
}

/**
 * Record that this seat has finished onboarding. Called only once the server's
 * roster confirms the seat has a photo — never optimistically.
 */
export function markOnboarded(roomCode: string): void {
  const code = normalise(roomCode);
  const map = readAll();
  const existing = map[code];
  if (!existing || existing.onboarded) return;
  map[code] = { ...existing, onboarded: true, updatedAt: Date.now() };
  writeAll(map);
}

/** Marks the session as still alive, so it does not age out mid-game. */
export function touchSession(roomCode: string): void {
  const code = normalise(roomCode);
  const map = readAll();
  const existing = map[code];
  if (!existing) return;
  map[code] = { ...existing, updatedAt: Date.now() };
  writeAll(map);
}

/**
 * Drop a seat. Called when the server rejects the token (the room was purged,
 * or the seat was removed by the host) — keeping a dead token would make every
 * future join fail the same way.
 */
export function clearSession(roomCode: string): void {
  const code = normalise(roomCode);
  const map = readAll();
  if (!(code in map)) return;
  delete map[code];
  writeAll(map);
}

export function clearAllSessions(): void {
  const store = storage();
  fallback.clear();
  try {
    store.removeItem(SESSION_STORAGE_KEY);
    store.removeItem(HOST_STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

/** Every room this device currently holds a seat in, most recent first. */
export function listSessions(): StoredSession[] {
  return Object.values(readAll()).sort((a, b) => b.updatedAt - a.updatedAt);
}

function normalise(roomCode: string): string {
  return roomCode.trim().toUpperCase();
}
